import { Controller } from '@hotwired/stimulus';

const TURBO_STREAM = 'text/vnd.turbo-stream.html';

interface TurboStreams {
  renderStreamMessage(html: string): void;
}

// Fallback for a progress frame whose broadcasts stop arriving: while the import runs and the tab
// is visible, re-render the frame from the cached progress. Every frame swap (broadcast or poll)
// re-arms the timer, so a healthy stream keeps the poller idle. Disconnecting stops it until the
// next connect: a request still in flight when the page navigates away must not re-arm it.
export default class extends Controller {
  static targets = ['frame'];
  static values = { url: String, jobKey: String, interval: { type: Number, default: 5000 } };

  declare readonly frameTarget: HTMLElement;
  declare readonly hasFrameTarget: boolean;
  declare readonly urlValue: string;
  declare readonly jobKeyValue: string;
  declare readonly intervalValue: number;

  private timer: number | undefined;
  private request: AbortController | undefined;
  private stopped = false;

  connect(): void {
    this.stopped = false;
    document.addEventListener('visibilitychange', this.onVisibilityChange);
    this.schedule();
  }

  disconnect(): void {
    this.stopped = true;
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
    this.clearTimer();
    this.request?.abort();
  }

  frameTargetConnected(): void {
    this.schedule();
  }

  private readonly onVisibilityChange = (): void => {
    if (document.visibilityState === 'visible') {
      void this.poll();
    } else {
      this.clearTimer();
    }
  };

  private get finished(): boolean {
    return this.stopped || (this.hasFrameTarget && this.frameTarget.dataset.terminal === 'true');
  }

  private schedule(): void {
    this.clearTimer();
    if (this.finished || document.visibilityState === 'hidden') return;

    this.timer = window.setTimeout(() => void this.poll(), this.intervalValue);
  }

  private async poll(): Promise<void> {
    this.clearTimer();
    if (this.finished || this.request || document.visibilityState === 'hidden') return;

    const request = new AbortController();
    this.request = request;
    try {
      const response = await fetch(this.progressUrl(), {
        headers: { Accept: TURBO_STREAM },
        credentials: 'same-origin',
        cache: 'no-store',
        signal: request.signal,
      });

      // Signed out, not allowed, or an unknown run: polling again cannot help.
      if (response.redirected || (response.status >= 400 && response.status < 500)) {
        this.stopped = true;
      } else if (response.ok && (response.headers.get('Content-Type') || '').includes(TURBO_STREAM)) {
        const html = await response.text();
        // A terminal broadcast may have landed while this request was in flight.
        if (!this.finished) this.turbo()?.renderStreamMessage(html);
      }
    } catch {
      // Offline or aborted: the next tick retries.
    } finally {
      if (this.request === request) this.request = undefined;
      this.schedule();
    }
  }

  private progressUrl(): string {
    const url = new URL(this.urlValue, window.location.href);
    url.searchParams.set('job_key', this.jobKeyValue);
    return url.toString();
  }

  private turbo(): TurboStreams | undefined {
    return (window as unknown as { Turbo?: TurboStreams }).Turbo;
  }

  private clearTimer(): void {
    if (this.timer !== undefined) window.clearTimeout(this.timer);
    this.timer = undefined;
  }
}
